/**
 * Procedural development map for the `?showcase=world` grey box (owner: env-builder).
 * Pure data builder — deterministic for a given seed via SeededRng. 40 × 24 tiles.
 *
 * Layer data follows the maps builder's convention: `-1` = empty, otherwise a 0-based frame index
 * into the layer's tileset (`DEV_TILES[i]`). Tileset id / texture key: `dev-world-tiles` (distinct
 * from the maps module's own `dev-tiles`).
 */
import { TILE } from '@/core/config';
import { SeededRng } from '@/core/rng';
import type { MapData, MapLayerData, MapNpcPlacement } from '@/core/types/contracts';

export const DEV_MAP_ID = 'dev-grey';
export const DEV_TILESET_KEY = 'dev-world-tiles';
export const DEV_MAP_W = 40;
export const DEV_MAP_H = 24;
export const EMPTY_TILE = -1;

/** Dev tile catalogue (frame index = array index). Colours are palette anchors from ART_DIRECTION.md. */
export const DEV_TILES = [
  { id: 0, name: 'grass', label: 'G', color: 0x3c7a3a, solid: false },
  { id: 1, name: 'sand', label: 'S', color: 0xe2c48c, solid: false },
  { id: 2, name: 'path', label: 'P', color: 0xc9b89a, solid: false },
  { id: 3, name: 'water', label: 'W', color: 0x1f8f8a, solid: true },
  { id: 4, name: 'wall', label: 'X', color: 0x8a5a32, solid: true },
  { id: 5, name: 'crate', label: 'C', color: 0x8c2a22, solid: true },
  { id: 6, name: 'roof', label: 'R', color: 0x2b3a5c, solid: false },
  { id: 7, name: 'pier', label: '=', color: 0xa87848, solid: false },
  { id: 8, name: 'tuft', label: 'v', color: 0x8ec95a, solid: false },
] as const;
export const T = { GRASS: 0, SAND: 1, PATH: 2, WATER: 3, WALL: 4, CRATE: 5, ROOF: 6, PIER: 7, TUFT: 8 } as const;

export const DEV_RECTS = {
  /** harbour water on the right, tile coords */
  water: { x: 30, y: 2, w: 9, h: 21 },
  /** walkable pier that crosses the water */
  pier: { x: 24, y: 11, w: 10, h: 2 },
  beach: { x: 26, y: 1, w: 4, h: 22 },
  /** building with an overhead roof (shelter) */
  house: { x: 8, y: 3, w: 6, h: 5 },
} as const;

function idx(x: number, y: number): number { return y * DEV_MAP_W + x; }
function inRect(x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean {
  return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
}

export function buildDevMap(seed = 1157): MapData {
  const rng = new SeededRng(seed);
  const size = DEV_MAP_W * DEV_MAP_H;
  const ground = new Array<number>(size).fill(T.GRASS);
  const detail = new Array<number>(size).fill(EMPTY_TILE);
  const overhead = new Array<number>(size).fill(EMPTY_TILE);
  const collision = new Array<number>(size).fill(0);

  for (let y = 0; y < DEV_MAP_H; y++) {
    for (let x = 0; x < DEV_MAP_W; x++) {
      const i = idx(x, y);
      const border = x === 0 || y === 0 || x === DEV_MAP_W - 1 || y === DEV_MAP_H - 1;
      if (border) { ground[i] = T.WALL; collision[i] = 1; continue; }
      if (inRect(x, y, DEV_RECTS.beach)) ground[i] = T.SAND;
      if (inRect(x, y, DEV_RECTS.water)) { ground[i] = T.WATER; collision[i] = 1; }
      if (inRect(x, y, DEV_RECTS.pier)) { ground[i] = T.PIER; collision[i] = 0; }
      // east-west path from the start spawn to the pier
      if ((y === 11 || y === 12) && x >= 3 && x < DEV_RECTS.pier.x) ground[i] = T.PATH;
      // north-south path to the house door
      if (x === 10 && y >= 8 && y <= 12) ground[i] = T.PATH;
      // house walls (door at the bottom middle)
      const h = DEV_RECTS.house;
      if (inRect(x, y, h)) {
        const edge = x === h.x || x === h.x + h.w - 1 || y === h.y || y === h.y + h.h - 1;
        const door = y === h.y + h.h - 1 && x === h.x + 2;
        if (edge && !door) { ground[i] = T.WALL; collision[i] = 1; } else ground[i] = T.PATH;
        if (y < h.y + h.h - 1) overhead[i] = T.ROOF; // roof covers everything but the bottom wall row
      }
      if (overhead[i] === EMPTY_TILE && ground[i] === T.GRASS && rng.chance(0.08)) detail[i] = T.TUFT;
    }
  }
  // crates (solid) near the market square, deterministic positions
  const crates: [number, number][] = [[18, 5], [19, 5], [18, 6], [22, 16], [23, 16], [6, 18], [7, 18], [7, 19]];
  for (const [cx, cy] of crates) { const i = idx(cx, cy); ground[i] = T.CRATE; collision[i] = 1; detail[i] = EMPTY_TILE; }
  // a few extra rng-placed crates on open grass (never on paths/pier/spawns)
  let placed = 0;
  while (placed < 4) {
    const x = rng.int(3, 24), y = rng.int(14, 21);
    const i = idx(x, y);
    if (ground[i] !== T.GRASS || collision[i] !== 0 || (x >= 3 && x <= 7 && y >= 10 && y <= 13)) continue;
    ground[i] = T.CRATE; collision[i] = 1; detail[i] = EMPTY_TILE; placed++;
  }

  const layers: MapLayerData[] = [
    { name: 'ground', tileset: DEV_TILESET_KEY, data: ground, depth: 'ground' },
    { name: 'detail', tileset: DEV_TILESET_KEY, data: detail, depth: 'detail' },
    { name: 'overhead', tileset: DEV_TILESET_KEY, data: overhead, depth: 'overhead' },
  ];

  const px = (tx: number) => tx * TILE + TILE / 2;
  const py = (ty: number) => ty * TILE + TILE - 4; // feet just above the tile's bottom edge
  const npcs: MapNpcPlacement[] = [
    {
      id: 'npc-trader', actor: 'yabei-merchant', x: px(20), y: py(8), facing: 'down',
      schedule: [
        { kind: 'wait', ms: 1200 }, { kind: 'walk', x: px(24), y: py(8) }, { kind: 'face', facing: 'left' },
        { kind: 'wait', ms: 900 }, { kind: 'walk', x: px(20), y: py(8) }, { kind: 'face', facing: 'down' }, { kind: 'loop' },
      ],
      weatherBehaviour: {
        rain: [{ kind: 'walk', x: px(10), y: py(6) }, { kind: 'face', facing: 'down' }, { kind: 'wait', ms: 4000 }, { kind: 'loop' }],
        'interior-rain': [{ kind: 'walk', x: px(10), y: py(6) }, { kind: 'face', facing: 'down' }, { kind: 'wait', ms: 4000 }, { kind: 'loop' }],
      },
    },
    {
      id: 'npc-sailor', actor: 'sailor', x: px(25), y: py(13), facing: 'right',
      schedule: [
        { kind: 'walk', x: px(32), y: py(12) }, { kind: 'face', facing: 'right' }, { kind: 'wait', ms: 1500 },
        { kind: 'walk', x: px(25), y: py(13) }, { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 1500 }, { kind: 'loop' },
      ],
    },
    {
      id: 'npc-elder', actor: 'villager-m', x: px(14), y: py(16), facing: 'up',
      schedule: [
        { kind: 'wait', ms: 2000 }, { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 2000 }, { kind: 'face', facing: 'right' },
        { kind: 'wait', ms: 2000 }, { kind: 'face', facing: 'up' }, { kind: 'loop' },
      ],
      weatherBehaviour: {
        rain: [{ kind: 'walk', x: px(12), y: py(6) }, { kind: 'face', facing: 'down' }, { kind: 'wait', ms: 6000 }, { kind: 'loop' }],
      },
    },
  ];

  const h = DEV_RECTS.house;
  return {
    id: DEV_MAP_ID,
    width: DEV_MAP_W,
    height: DEV_MAP_H,
    tileSize: 32,
    layers,
    collision,
    // east end of the pier: walking onto it fires onExit (self-targeting so the grey box needs no other map)
    exits: [{ id: 'to-sea', x: (DEV_RECTS.pier.x + DEV_RECTS.pier.w - 1) * TILE, y: DEV_RECTS.pier.y * TILE, w: TILE, h: DEV_RECTS.pier.h * TILE, target: DEV_MAP_ID, spawn: 'pier' }],
    spawns: [
      { id: 'start', x: px(5), y: py(12), facing: 'right' },
      { id: 'pier', x: px(26), y: py(12), facing: 'right' },
      { id: 'house', x: px(10), y: py(9), facing: 'down' },
    ],
    interactions: [
      // two tiles north of the start spawn: reachable by walking up once, for the interaction demo
      { id: 'signpost', x: 5 * TILE, y: 9 * TILE, w: TILE, h: 2 * TILE, kind: 'observe', label: '看看路牌', dialogue: 'dev-observe-signpost' },
      { id: 'crates', x: 18 * TILE, y: 7 * TILE, w: 2 * TILE, h: TILE, kind: 'observe', label: '看看货箱', dialogue: 'dev-observe-crates' },
      { id: 'water-edge', x: 29 * TILE, y: 4 * TILE, w: TILE, h: 6 * TILE, kind: 'observe', label: '望向海面', dialogue: 'dev-observe-water' },
      { id: 'house-door', x: (DEV_RECTS.house.x + 2) * TILE, y: (DEV_RECTS.house.y + DEV_RECTS.house.h - 1) * TILE, w: TILE, h: TILE, kind: 'trigger', label: '进屋避雨', cinematic: 'dev-enter-house' },
    ],
    npcs,
    lights: [{ x: px(10), y: h.y * TILE + 2 * TILE, radius: 72, color: '#ffb347', intensity: 0.8, flicker: true }],
    water: [{ x: DEV_RECTS.water.x * TILE, y: DEV_RECTS.water.y * TILE, w: DEV_RECTS.water.w * TILE, h: DEV_RECTS.water.h * TILE }],
    rainShelter: [{ x: h.x * TILE, y: h.y * TILE, w: h.w * TILE, h: (h.h - 1) * TILE }],
    music: 'dev-bgm-harbor',
    ambience: ['dev-amb-sea'],
  };
}

/** Tile-space collision lookup for tests and tools. */
export function tileSolid(map: MapData, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) return true;
  return (map.collision[ty * map.width + tx] ?? 1) !== 0;
}
